import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Switch,
  Grid,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  TrendingUp as StatsIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  SmartToy as SmartToyIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Chat as ChatIcon,
  Language as WebIcon,
} from "@mui/icons-material";
import {
  getAutomationRulesApi,
  deleteAutomationRuleApi,
  toggleAutomationRuleApi,
  testAutomationRuleApi,
} from "../../api/automation.api";
import { EVENT_CATEGORIES, getEventLabel } from "../../constants/automationEvents";
import CreateRuleModal from "./components/CreateRuleModal";
import RuleLogsDialog from "./components/RuleLogsDialog";
import TestResultDialog from "./components/TestResultDialog";

const CHANNEL_TABS = [
  { label: "All", value: "all" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Website", value: "website" },
];

const AutomationRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterEnabled, setFilterEnabled] = useState("all");
  const [channelTab, setChannelTab] = useState("all");

  // Dialog states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  // Test result dialog
  const [testResultOpen, setTestResultOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  useEffect(() => {
    fetchRules();
  }, [filterEnabled]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filterEnabled !== "all") {
        params.enabled = filterEnabled === "enabled";
      }
      const response = await getAutomationRulesApi(params);
      setRules(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load automation rules");
      console.error("Error fetching rules:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (ruleId) => {
    try {
      await toggleAutomationRuleApi(ruleId);
      await fetchRules();
    } catch (err) {
      console.error("Error toggling rule:", err);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this automation rule?")) {
      return;
    }
    try {
      await deleteAutomationRuleApi(ruleId);
      await fetchRules();
      handleMenuClose();
    } catch (err) {
      console.error("Error deleting rule:", err);
    }
  };

  const handleTestRule = async (ruleId) => {
    try {
      handleMenuClose();
      const response = await testAutomationRuleApi(ruleId);
      const result = response.data.data.testResult;
      setTestResult(result);
      setTestResultOpen(true);
    } catch (err) {
      console.error("Error testing rule:", err);
      setTestResult({
        status: "failed",
        error: err.response?.data?.message || err.message,
      });
      setTestResultOpen(true);
    }
  };

  const handleMenuOpen = (event, rule) => {
    setAnchorEl(event.currentTarget);
    setSelectedRule(rule);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRule(null);
  };

  const handleEditClick = () => {
    setEditRule(selectedRule);
    setCreateModalOpen(true);
    handleMenuClose();
  };

  const handleLogsClick = () => {
    setSelectedRuleId(selectedRule._id);
    setLogsDialogOpen(true);
    handleMenuClose();
  };

  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditRule(null);
    fetchRules();
  };

  const getChannelIcon = (channelType) => {
    switch (channelType) {
      case "whatsapp":
        return "\uD83D\uDCAC";
      case "email":
        return "\uD83D\uDCE7";
      case "sms":
        return "\uD83D\uDCF1";
      default:
        return "\uD83D\uDCE2";
    }
  };

  const getChannelLabel = (channelType) => {
    switch (channelType) {
      case "whatsapp":
        return "WhatsApp";
      case "email":
        return "Email";
      case "sms":
        return "SMS";
      default:
        return channelType;
    }
  };

  const filteredRules = rules.filter((rule) => {
    let matches = true;

    // Channel tab filter
    if (channelTab !== "all") {
      matches = rule.action?.channel?.type === channelTab;
    }

    // Search filter
    if (matches && search) {
      const searchLower = search.toLowerCase();
      matches =
        rule.name.toLowerCase().includes(searchLower) ||
        rule.description?.toLowerCase().includes(searchLower) ||
        rule.trigger.event.toLowerCase().includes(searchLower);
    }

    return matches;
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ fontSize: "1.45rem", lineHeight: 1.3 }}
          >
            Automation Rules
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, fontSize: "0.82rem" }}
          >
            Configure automatic messages based on lead events
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.8rem",
          }}
        >
          Create Rule
        </Button>
      </Box>

      {/* Channel Tabs */}
      <Tabs
        value={channelTab}
        onChange={(_, val) => setChannelTab(val)}
        sx={{
          minHeight: 36,
          "& .MuiTabs-indicator": {
            height: 2.5,
            borderRadius: 2,
          },
          "& .MuiTab-root": {
            textTransform: "capitalize",
            fontWeight: 500,
            fontSize: "0.85rem",
            minHeight: 36,
            px: 1.5,
            py: 0,
            mr: 1,
            minWidth: "auto",
          },
          "& .Mui-selected": {
            fontWeight: 600,
          },
        }}
      >
        {CHANNEL_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      <Divider sx={{ mb: 2.5, ml: -3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* Rules Card (Filters + Content) */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Filters */}
        <Box sx={{ px: 2.5, py: 1.2 }}>
          <Box
            display="flex"
            alignItems="center"
            gap={3}
          >
            {/* Search */}
            <TextField
              placeholder="Search rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 18, color: "text.disabled" }}
                    />
                  </InputAdornment>
                ),
                disableUnderline: true,
                sx: {
                  bgcolor: "#F8FAFC",
                  borderRadius: 3,
                  px: 1.5,
                  py: 0.5,
                },
              }}
              sx={{ minWidth: 220, maxWidth: 300 }}
            />

            {/* Status filter */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "#9ca3af",
                  fontSize: "0.72rem",
                  letterSpacing: 1.5,
                }}
              >
                STATUS:
              </Typography>
              <Select
                value={filterEnabled}
                onChange={(e) => setFilterEnabled(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  color: "text.primary",
                  bgcolor: "#F8FAFC",
                  borderRadius: 3,
                  pl: 2,
                  pr: 0.5,
                  "& .MuiSelect-select": {
                    py: 0.9,
                    pr: "32px !important",
                  },
                  "& .MuiSelect-icon": {
                    color: "#6b7280",
                    right: 8,
                  },
                }}
              >
                <MenuItem value="all">All Rules</MenuItem>
                <MenuItem value="enabled">Enabled Only</MenuItem>
                <MenuItem value="disabled">Disabled Only</MenuItem>
              </Select>
            </Box>

            {/* Count */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: "auto", fontSize: "0.82rem" }}
            >
              {filteredRules.length} rule
              {filteredRules.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Content */}
        <Box sx={{ minHeight: 350 }}>
        {filteredRules.length > 0 ? (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {filteredRules.map((rule) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={rule._id}>
                  <Card
                    sx={{
                      height: "100%",
                      position: "relative",
                      border: "1px solid",
                      borderColor: "#e5e7eb",
                      borderRadius: 4,
                      boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                      opacity: rule.settings.enabled ? 1 : 0.65,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderTop: "3px solid",
                        borderTopColor: "primary.main",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 0 } }}>
                      {/* Header: Title + Channel Chip + Toggle + Menu */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.75}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.05rem",
                              lineHeight: 1.3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rule.name}
                          </Typography>
                          <Chip
                            size="small"
                            icon={
                              rule.action.channel.type === "whatsapp"
                                ? <ChatIcon sx={{ fontSize: 13 }} />
                                : <WebIcon sx={{ fontSize: 13 }} />
                            }
                            label={getChannelLabel(rule.action.channel.type).toUpperCase()}
                            sx={{
                              height: 28,
                              px: 0.5,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              bgcolor:
                                rule.action.channel.type === "whatsapp"
                                  ? "#ecfdf5"
                                  : "grey.50",
                              color:
                                rule.action.channel.type === "whatsapp"
                                  ? "#16a34a"
                                  : "text.secondary",
                              border: "1px solid",
                              borderColor:
                                rule.action.channel.type === "whatsapp"
                                  ? "#bbf7d0"
                                  : "#e5e7eb",
                              borderRadius: "14px",
                              "& .MuiChip-icon": {
                                ml: 0.3,
                                color:
                                  rule.action.channel.type === "whatsapp"
                                    ? "#16a34a"
                                    : "text.secondary",
                              },
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.25}
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          <Switch
                            checked={rule.settings.enabled}
                            onChange={() => handleToggleEnabled(rule._id)}
                            size="small"
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, rule)}
                            sx={{ color: "#9ca3af" }}
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Description */}
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          color: "#6b7280",
                          mb: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rule.description || "No description"}
                      </Typography>

                      {/* Trigger */}
                      <Box mb={1.5}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "#9ca3af",
                            fontSize: "0.62rem",
                            letterSpacing: 1.2,
                            mb: 0.5,
                          }}
                        >
                          Trigger
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                          <Chip
                            label={getEventLabel(rule.trigger.event)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 26,
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              borderColor: "#e5e7eb",
                              borderRadius: 1.5,
                            }}
                          />
                          {rule.trigger.conditions &&
                            rule.trigger.conditions.length > 0 && (
                              <Chip
                                label={`${rule.trigger.conditions.length} condition${rule.trigger.conditions.length > 1 ? "s" : ""}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.62rem",
                                  bgcolor: "#f3f4f6",
                                  color: "#6b7280",
                                }}
                              />
                            )}
                        </Box>
                      </Box>

                      {/* Template */}
                      <Box mb={2}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "#9ca3af",
                            fontSize: "0.62rem",
                            letterSpacing: 1.2,
                            mb: 0.5,
                          }}
                        >
                          Template
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontFamily: "monospace",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {rule.action.channel.whatsapp?.templateName ||
                            "Not configured"}
                        </Typography>
                      </Box>

                      {/* Stats */}
                      <Divider sx={{ borderColor: "#f3f4f6" }} />
                      <Box
                        display="flex"
                        sx={{ py: 1.5 }}
                      >
                        <Box sx={{ flex: 1, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              textTransform: "uppercase",
                              color: "#9ca3af",
                              fontSize: "0.58rem",
                              letterSpacing: 1,
                              mb: 0.25,
                            }}
                          >
                            Triggered
                          </Typography>
                          <Typography
                            sx={{ fontSize: "1.05rem", fontWeight: 700 }}
                          >
                            {rule.metadata.triggerCount || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              textTransform: "uppercase",
                              color: "#9ca3af",
                              fontSize: "0.58rem",
                              letterSpacing: 1,
                              mb: 0.25,
                            }}
                          >
                            Success
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "1.05rem",
                              fontWeight: 700,
                              color: "#16a34a",
                            }}
                          >
                            {rule.metadata.successCount || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              textTransform: "uppercase",
                              color: "#9ca3af",
                              fontSize: "0.58rem",
                              letterSpacing: 1,
                              mb: 0.25,
                            }}
                          >
                            Failed
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "1.05rem",
                              fontWeight: 700,
                              color: "#dc2626",
                            }}
                          >
                            {rule.metadata.failureCount || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          /* Empty State */
          !loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 10,
                gap: 1.5,
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: (theme) => `${theme.palette.primary.main}18`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <SmartToyIcon
                  sx={{ fontSize: 36, color: "primary.main" }}
                />
              </Box>

              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: "1.05rem" }}
              >
                No automation rules found
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  maxWidth: 380,
                  lineHeight: 1.6,
                  fontSize: "0.84rem",
                  textAlign: "center",
                }}
              >
                {search
                  ? "Try a different search term"
                  : "Create your first automation rule to automate customer engagement and increase conversions."}
              </Typography>

              {!search && (
                <Button
                  startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setCreateModalOpen(true)}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                >
                  Create your first rule
                </Button>
              )}
            </Box>
          )
        )}
        </Box>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            minWidth: 180,
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            py: 0.5,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleEditClick}
          sx={{ py: 1, px: 2, gap: 1.5, fontSize: "0.85rem" }}
        >
          <EditIcon sx={{ fontSize: 18, color: "#6b7280" }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleTestRule(selectedRule?._id)}
          sx={{ py: 1, px: 2, gap: 1.5, fontSize: "0.85rem" }}
        >
          <TestIcon sx={{ fontSize: 18, color: "#6b7280" }} />
          Test
        </MenuItem>
        <MenuItem
          onClick={handleLogsClick}
          sx={{ py: 1, px: 2, gap: 1.5, fontSize: "0.85rem" }}
        >
          <HistoryIcon sx={{ fontSize: 18, color: "#6b7280" }} />
          View Logs
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => handleDeleteRule(selectedRule?._id)}
          sx={{ py: 1, px: 2, gap: 1.5, fontSize: "0.85rem", color: "#dc2626" }}
        >
          <DeleteIcon sx={{ fontSize: 18, color: "#dc2626" }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Modal */}
      <CreateRuleModal
        open={createModalOpen}
        onClose={handleModalClose}
        editRule={editRule}
      />

      {/* Logs Dialog */}
      <RuleLogsDialog
        open={logsDialogOpen}
        onClose={() => {
          setLogsDialogOpen(false);
          setSelectedRuleId(null);
        }}
        ruleId={selectedRuleId}
      />

      <TestResultDialog
        open={testResultOpen}
        onClose={() => {
          setTestResultOpen(false);
          setTestResult(null);
        }}
        result={testResult}
      />
    </Box>
  );
};

export default AutomationRules;
